/**
 * Flight payment screen view model.
 * Reads the selected flight, passengers, real baggage selections, and BookingPriceViewModel.
 * Does not read hotel, room, nights, or package pricing.
 */

import { formatFlightReviewDisplayMoney } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/resolveFlightReviewDisplayMoney";
import { legacyAppRound2 } from "../../../pricing/pricingEngine.js";
import { ceilLdsPaymentRemainingAmount } from "../../../utils/orders/ldsPaymentAmountRounding";
import { getFlightsNewProposalPriceBreakdownIls } from "../../../utils/dynamicPackageDisplayMode";
import { normalizeLdsOrderLinkedFlights } from "../../../utils/orders/normalizeLdsOrderLinkedFlights";
import { mapBookingPriceViewModel } from "../view-models/mappers/mapBookingPriceViewModel";

const HOTEL_FIELD_KEYS = new Set([
  "room",
  "roomType",
  "numberOfRooms",
  "nights",
  "hotel",
  "hotelName",
  "packageSummary",
  "packagePricing",
  "checkIn",
  "checkOut",
]);

/**
 * @param {{
 *   offer?: object|null,
 *   passengers?: object[]|null,
 *   extrasCatalog?: object|null,
 *   baggageLines?: object[]|null,
 *   price?: import("../view-models/types").BookingPriceViewModel|null,
 * }} input
 */
export function mapFlightPaymentViewModel(input = {}) {
  const offer = input.offer && typeof input.offer === "object" ? input.offer : null;
  const price = input.price && typeof input.price === "object" ? input.price : {};
  const airlineName = String(offer?.airline?.name || offer?.airline?.code || "").trim();
  const outbound = legBlock(offer?.outbound, "הלוך", airlineName);
  const inbound = legBlock(offer?.inbound, "חזור", airlineName);
  const baggageLines = Array.isArray(input.baggageLines)
    ? input.baggageLines.filter(isRealBaggageLine)
    : baggageLinesFromCatalog(input.extrasCatalog);

  const summary = summaryFromPrice(price, input.passengers, baggageLines);

  return stripHotelFields({
    title: "פרטי הטיסה",
    routeLabel: routeLabel(offer, outbound),
    datesLabel: datesLabel(offer, outbound, inbound),
    passengersLabel: passengersLabel(input.passengers),
    outbound,
    inbound,
    baggageLines,
    summary,
  });
}

/**
 * Persisted flight-only order → the same view model.
 * Payable comes from the stored flight fare via BookingPriceViewModel, not hotel reprice.
 * @param {{ dynamicOrder?: object|null, orderReqData?: object|null }} input
 */
export function mapFlightPaymentViewModelFromOrder(input = {}) {
  const dynamicOrder = input.dynamicOrder && typeof input.dynamicOrder === "object" ? input.dynamicOrder : null;
  const orderReqData = input.orderReqData && typeof input.orderReqData === "object" ? input.orderReqData : null;
  const flights =
    (dynamicOrder?.flights && typeof dynamicOrder.flights === "object" ? dynamicOrder.flights : null) ||
    (orderReqData?.flights && typeof orderReqData.flights === "object" ? orderReqData.flights : null) ||
    null;
  const breakdown = getFlightsNewProposalPriceBreakdownIls(flights);
  const baggageLines = checkedSupplementLines(flights);
  const extrasIls = baggageLines.reduce((sum, line) => {
    const n = Number(line.amountRaw != null ? line.amountRaw : line.amount);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  const price = mapBookingPriceViewModel({
    offer: null,
    pricing: {
      total: breakdown.totalIls > 0 ? breakdown.totalIls : null,
      currency: breakdown.totalIls > 0 ? "ILS" : null,
      handlingFee: breakdown.handlingIls > 0 ? breakdown.handlingIls : null,
      flight: breakdown.baseIls > 0 ? breakdown.baseIls : null,
      extras: extrasIls > 0 ? extrasIls : null,
      baggageTotal: extrasIls > 0 ? extrasIls : null,
    },
  });
  const linked =
    flights?.linkedflights ||
    flights?.linkedFlights ||
    orderReqData?.linkedflights ||
    orderReqData?.linkedFlights ||
    null;
  const normalized = linked
    ? normalizeLdsOrderLinkedFlights({ linkedflights: linked })
    : [];
  const offer = offerFromNormalizedLegs(normalized);
  return mapFlightPaymentViewModel({
    offer,
    passengers: passengersFromFlights(flights),
    baggageLines,
    price,
  });
}

export function flightPaymentPayableAmount(model) {
  const n = Number(model?.summary?.payableAmount);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function summaryFromPrice(price, passengers, baggageLines) {
  const sourceCurrency = currencyCode(price.sourceCurrency);
  // Payment boundary only: whole shekel, rounded down once. Display and the
  // submitted charge both read payableAmount. Not legacyAppRound2 / Math.ceil.
  const rawPayable = finite(price.payableIlsTotal);
  const payableAmount = rawPayable == null ? null : Math.floor(rawPayable);
  const rate = finite(price.pricingFxRate);
  const list = Array.isArray(passengers) ? passengers : [];
  const rows = [];

  if (list.length > 0) {
    const perPersonUsd = resolvePerPassengerUsd(price, list);
    list.forEach((pax, index) => {
      const usd = perPersonUsd ? perPersonUsd[index] : null;
      // Same ceil already applied to calculatedIlsTotal. Once, display only.
      const ils = usd != null && rate != null ? legacyAppRound2(usd * rate) : null;
      rows.push({
        key: `passenger-${index + 1}`,
        label: passengerDisplayName(pax, index),
        value: dualMoneyLabel(usd, ils),
      });
    });
  } else {
    const flightLabel =
      finite(price.clientFlightTotal) != null && sourceCurrency
        ? formatFlightReviewDisplayMoney(price.clientFlightTotal, sourceCurrency)
        : finite(price.flightBaseTotal) != null
          ? formatFlightReviewDisplayMoney(price.flightBaseTotal, "ILS")
          : null;
    if (flightLabel) rows.push({ key: "flight", label: "מחיר טיסה", value: flightLabel });
  }

  const extrasPair = extrasDualAmounts(price, rate);
  const ceiledLineSum = sumCeiledBaggageAmounts(baggageLines);
  if (extrasPair && ceiledLineSum != null) {
    extrasPair.ils = ceiledLineSum;
  }
  if (extrasPair) {
    const extrasLabel = dualMoneyLabel(extrasPair.usd, extrasPair.ils);
    if (extrasLabel) rows.push({ key: "extras", label: "כבודה / תוספות", value: extrasLabel });
  }

  const handling = finite(price.handlingFee);
  if (handling != null && handling !== 0) {
    const handlingUsd = rate != null && rate !== 0 ? handling / rate : null;
    const handlingLabel = dualMoneyLabel(handlingUsd, handling);
    if (handlingLabel) rows.push({ key: "handling", label: "דמי טיפול", value: handlingLabel });
  }

  if (list.length === 0 && sourceCurrency && finite(price.sourceCurrencyTotal) != null) {
    const sourceLabel = formatFlightReviewDisplayMoney(price.sourceCurrencyTotal, sourceCurrency);
    if (sourceLabel) rows.push({ key: "source", label: "סה״כ במטבע המקור", value: sourceLabel });
  }

  if (rate != null && sourceCurrency) {
    rows.push({
      key: "rate",
      label: "שער לחישוב",
      value: `${sourceCurrency} → ILS ${rate}`,
    });
  }

  return {
    rows,
    payableAmount,
    payableLabel:
      payableAmount != null ? formatFlightReviewDisplayMoney(payableAmount, "ILS") : null,
  };
}

/**
 * Uniform customer per-person USD, or a stored per-passenger USD list.
 * Does not divide the trip total across passengers.
 * @returns {Array<number|null>|null}
 */
function resolvePerPassengerUsd(price, passengers) {
  const bd = price?.passengerPriceBreakdown;
  const storedRows = Array.isArray(bd)
    ? bd
    : bd && typeof bd === "object" && Array.isArray(bd.passengers)
      ? bd.passengers
      : null;
  if (storedRows && storedRows.length > 0) {
    const amounts = passengers.map((_, index) => readUsdAmount(storedRows[index]));
    if (amounts.some((amount) => amount != null)) return amounts;
  }

  const fromPassengers = passengers.map((pax) => readUsdAmount(pax));
  if (fromPassengers.some((amount) => amount != null)) return fromPassengers;

  const uniform =
    bd && typeof bd === "object" && !Array.isArray(bd) ? finite(bd.perPersonUsd) : null;
  if (uniform != null) return passengers.map(() => uniform);
  return null;
}

function readUsdAmount(row) {
  if (!row || typeof row !== "object") return null;
  return finite(row.priceUsd ?? row.amountUsd ?? row.perPersonUsd);
}

function sumCeiledBaggageAmounts(baggageLines) {
  const lines = Array.isArray(baggageLines) ? baggageLines : [];
  const amounts = lines
    .filter((line) => finite(line?.amountRaw) != null)
    .map((line) => finite(line.amount))
    .filter((amount) => amount != null && amount > 0);
  if (!amounts.length) return null;
  return amounts.reduce((sum, amount) => sum + amount, 0);
}

function extrasDualAmounts(price, rate) {
  const amount = finite(price.extrasTotal) ?? finite(price.baggageTotal);
  const currency = currencyCode(price.extrasCurrency);
  if (amount == null || amount <= 0 || !currency) return null;
  if (currency === "USD") {
    const ils = rate != null ? amount * rate : null;
    return {
      usd: amount,
      ils: ils != null && ils > 0 ? ceilLdsPaymentRemainingAmount(ils) : ils,
    };
  }
  if (currency === "ILS") {
    return {
      ils: ceilLdsPaymentRemainingAmount(amount),
      usd: rate != null && rate !== 0 ? amount / rate : null,
    };
  }
  return null;
}

function dualMoneyLabel(usd, ils) {
  const parts = [];
  const usdLabel = usd != null ? formatFlightReviewDisplayMoney(usd, "USD") : null;
  const ilsLabel = ils != null ? formatFlightReviewDisplayMoney(ils, "ILS") : null;
  if (usdLabel) parts.push(usdLabel);
  if (ilsLabel) parts.push(ilsLabel);
  return parts.length ? parts.join(" · ") : null;
}

function passengerDisplayName(pax, index) {
  const composed = [pax?.firstName, pax?.middleName, pax?.lastName]
    .filter((part) => String(part || "").trim())
    .join(" ");
  if (composed) return composed;
  const named = String(pax?.name || pax?.fullname || "").trim();
  if (named) return named;
  return `נוסע ${index + 1}`;
}

function baggageLinesFromCatalog(catalog) {
  if (!catalog || typeof catalog !== "object") return [];
  const options = []
    .concat(catalog.baggageOptions || [], catalog.otherExtras || [])
    .filter((row) => row && row.id != null);
  const optionsById = new Map(options.map((row) => [row.id, row]));
  const directions =
    catalog.directionMode === "whole-trip"
      ? [
          {
            id: "whole",
            label: catalog.directions?.[0]?.label || "כל ההזמנה",
          },
        ]
      : catalog.directions || [];
  const lines = [];
  for (const pax of catalog.passengers || []) {
    const selected = catalog.selections?.[pax.id] || {};
    for (const dir of directions) {
      const ids = directionIds(selected[dir.id]);
      for (const optId of ids) {
        const opt = optionsById.get(optId);
        if (!opt || opt.state === "included" || opt.state === "unavailable") continue;
        lines.push({
          passengerName: String(pax.name || "").trim(),
          directionLabel: String(dir.label || "").trim(),
          itemLabel: String(opt.name || "").trim(),
          amountLabel: opt.priceLabel || null,
        });
      }
    }
  }
  return lines.filter(isRealBaggageLine);
}

function checkedSupplementLines(flights) {
  const supplement = flights?.supplement;
  const entries = Array.isArray(supplement)
    ? supplement
    : supplement && typeof supplement === "object"
      ? Object.values(supplement)
      : [];
  const lines = [];
  for (const entry of entries) {
    if (!entry || typeof entry !== "object" || entry.checked !== true) continue;
    const passengerName = [entry?.pax?.firstname, entry?.pax?.lastname]
      .filter((part) => String(part || "").trim())
      .join(" ");
    const from = String(entry?.flight?.depCity || "").trim();
    const to = String(entry?.flight?.arrCity || "").trim();
    const amountRaw = finite(entry.ourFare);
    const amount =
      amountRaw != null && amountRaw > 0 ? ceilLdsPaymentRemainingAmount(amountRaw) : amountRaw;
    const usd = finite(entry.fareUsd ?? entry.ourFareUsd ?? entry.orig?.supp?.fareUsd);
    const ilsLabel = amount != null ? formatFlightReviewDisplayMoney(amount, "ILS") : null;
    const usdLabel = usd != null && usd > 0 ? formatFlightReviewDisplayMoney(usd, "USD") : null;
    lines.push({
      passengerName,
      directionLabel: from && to ? `${from} → ${to}` : "",
      itemLabel: String(entry.description || entry.type || "").trim(),
      amount,
      amountRaw,
      amountLabel:
        ilsLabel && usdLabel ? `${ilsLabel} (${usdLabel})` : ilsLabel,
    });
  }
  return lines.filter(isRealBaggageLine);
}

function isRealBaggageLine(line) {
  if (!line || typeof line !== "object") return false;
  return Boolean(String(line.itemLabel || "").trim() || String(line.passengerName || "").trim());
}

function offerFromNormalizedLegs(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return null;
  const outbound = normalizedLeg(list[0]);
  const inbound = list[1] ? normalizedLeg(list[1]) : null;
  return {
    airline: { name: outbound?.airline || inbound?.airline || "" },
    outbound,
    inbound,
  };
}

function normalizedLeg(row) {
  if (!row) return null;
  return {
    depIata: row.depCode || "",
    arrIata: row.arrCode || "",
    depTime: row.depTime || "",
    arrTime: row.arrTime || "",
    date: row.depDate || "",
    flightNumber: row.flightNumber || "",
    airline: row.airline || "",
  };
}

function passengersFromFlights(flights) {
  const groups = Array.isArray(flights?.passengersinfo) ? flights.passengersinfo : [];
  const people = [];
  for (const group of groups) {
    for (const adult of group?.adults || []) {
      people.push({ role: "adult", firstName: adult?.firstname, lastName: adult?.lastname });
    }
    for (const child of group?.kids || []) {
      people.push({ role: "child", firstName: child?.firstname, lastName: child?.lastname });
    }
    for (const infant of group?.infants || []) {
      people.push({ role: "infant", firstName: infant?.firstname, lastName: infant?.lastname });
    }
  }
  return people;
}

function legBlock(leg, directionLabel, airlineFallback) {
  if (!leg || typeof leg !== "object") return null;
  const from = text(leg.depIata || leg.departure?.airport);
  const to = text(leg.arrIata || leg.arrival?.airport);
  const flightNumber = text(leg.flightNumber);
  const depTime = text(leg.depTime || leg.departure?.time);
  const arrTime = text(leg.arrTime || leg.arrival?.time);
  const airline = text(leg.airlineName || leg.airline || airlineFallback);
  if (!from && !to && !flightNumber && !depTime && !arrTime) return null;
  return {
    directionLabel,
    airline,
    flightNumber,
    routeLabel: [from, to].filter(Boolean).join(" → "),
    depTime,
    arrTime,
    dateLabel: text(leg.date),
  };
}

function routeLabel(offer, outbound) {
  const from = text(offer?.outbound?.depIata || offer?.outbound?.departure?.airport || "");
  const to = text(offer?.outbound?.arrIata || offer?.outbound?.arrival?.airport || "");
  if (from && to) return `${from} → ${to}`;
  return outbound?.routeLabel || "";
}

function datesLabel(offer, outbound, inbound) {
  const depart = text(offer?.outbound?.date || outbound?.dateLabel);
  const ret = text(offer?.inbound?.date || inbound?.dateLabel);
  return [depart, ret].filter(Boolean).join(" – ");
}

function passengersLabel(passengers) {
  const list = Array.isArray(passengers) ? passengers : [];
  if (!list.length) return "";
  let adults = 0;
  let children = 0;
  let infants = 0;
  for (const pax of list) {
    const role = String(
      pax?.role || pax?.type || pax?.passengerType || pax?.typeLabel || ""
    ).toLowerCase();
    if (role.includes("infant") || role.includes("תינוק")) infants += 1;
    else if (
      role.includes("child") ||
      role.includes("kid") ||
      role.includes("ילד") ||
      pax?.isKid === true
    ) {
      children += 1;
    } else adults += 1;
  }
  const parts = [];
  if (adults) parts.push(adults === 1 ? "מבוגר 1" : `${adults} מבוגרים`);
  if (children) parts.push(children === 1 ? "ילד 1" : `${children} ילדים`);
  if (infants) parts.push(infants === 1 ? "תינוק 1" : `${infants} תינוקות`);
  return parts.join(", ");
}

function directionIds(slot) {
  if (Array.isArray(slot)) return slot;
  if (slot) return [slot];
  return [];
}

function stripHotelFields(value) {
  if (Array.isArray(value)) return value.map(stripHotelFields);
  if (!value || typeof value !== "object") return value;
  const next = {};
  for (const [key, child] of Object.entries(value)) {
    if (HOTEL_FIELD_KEYS.has(key)) continue;
    next[key] = stripHotelFields(child);
  }
  return next;
}

function currencyCode(raw) {
  if (raw == null || typeof raw === "object") return null;
  const c = String(raw).trim().toUpperCase();
  if (!c) return null;
  if (c === "$" || c === "US$") return "USD";
  if (c === "₪" || c === "NIS") return "ILS";
  return c;
}

function finite(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function text(value) {
  return String(value || "").trim();
}
